<template>
    <div>
        <div class="card shadow">
            <div class="card-header bg-transparent">
                <div>
                    <h3 class="mb-0">
                        {{ $tc('message.conclusions', 2) }}
                    </h3>
                </div>
            </div>

            <div class="card-body">
                <div class="card-title">
                    <router-link :to="{ name: 'add-conclusion' }" v-if="can">
                        {{ $t('message.add') }}

                        <i class="fas fa-plus-circle edit-icon"></i>
                    </router-link>
                </div>

                <template v-if="rows">

                    <b-container>
                        <b-row class="justify-content-sm-center">
                            <b-col col sm="6">
                                <b-form-group>
                                    <autocomplete
                                            ref="autocomplete"
                                            :placeholder="$t('message.search')"
                                            :source="mConclusions"
                                            input-class="form-control"
                                            results-property="data"
                                            :results-display="formattedDisplay"
                                            @selected="itemSelected"
                                    ></autocomplete>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </b-container>

                    <b-table
                            id="conclusions-table"
                            primary-key="id"
                            :per-page="perPage"
                            :current-page="currentPage"
                            selectable
                            select-mode="single"
                            borderless
                            responsive
                            striped
                            hover
                            :sort-by.sync="sortBy"
                            :sort-desc.sync="sortDesc"
                            @row-selected="rowSelected"
                            :fields="fields"
                            :items="mConclusions"
                    >
                        <!-- A virtual composite column for actions -->
                        <template slot="action" slot-scope="data">
                            <div class="row">
                                <div class="col-sm-6">
                                    <b-link :to="{ name: 'edit-conclusion', params: { conclusion: data.item.slug } }">
                                        <i class="fas fa-edit fa-2x edit-icon"></i>
                                    </b-link>
                                </div>

                                <div class="col-sm-6">
                                    <i v-b-modal="'delete-modal-' + data.item.slug" class="fas fa-trash fa-2x delete-icon"></i>
                                </div>
                            </div>

                            <b-modal
                                    :id="'delete-modal-' + data.item.slug"
                                    size="sm"
                                    header-bg-variant="danger"
                                    header-text-variant="light"
                                    :title="$t('message.delete') + ' ' + $tc('message.conclusions', 1)"
                                    hide-footer
                            >
                                <div class="d-block text-center">
                                    <h3>{{ $t('message.delete') }} {{ data.item.reference }}?</h3>
                                </div>

                                <div class="d-block float-right" v-if="data.item.isAuthor || hasAdminRole">
                                    <b-button class="secondary" @click="deleteConclusion(data.item.slug)">
                                        {{ $t('message.delete') }}
                                    </b-button>

                                    <b-button class="danger" @click="$bvModal.hide('delete-modal-' + data.item.slug)">
                                        {{ $t('message.cancel') }}
                                    </b-button>
                                </div>
                            </b-modal>
                        </template>
                    </b-table>

                    <b-pagination
                            class="justify-content-center"
                            v-model="currentPage"
                            :total-rows="rows"
                            :per-page="perPage"
                            aria-controls="conclusions-table"
                    ></b-pagination>
                </template>

                <template v-else>
                    <div class="card">
                        <div class="card-body text-xs-center">
                            <p class="card-text">
                                {{ $t('message.noResults') }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import moment from 'moment';
    import _ from 'lodash';

    export default {
        name: "Conclusions",

        computed: {
            ...mapGetters(["conclusions", "hasAdminRole", "hasMedecinControleRole", "hasPraticienRole","hasAssistante"]),

            can: function (){
                if (this.hasAdminRole){
                    return true;
                }else{
                    return this.hasMedecinControleRole || this.hasPraticienRole || this.hasAssistante;
                }
            },

            mConclusions: function() {
                let cArray = [];

                _.forEach(this.conclusions, function(conclusion) {
                    let c = {};

                    c.reference = conclusion.reference;
                    c.description = conclusion.description.length > 20 ? conclusion.description.substr(0, 20) + '...' : conclusion.description;
                    c.date = moment(conclusion.created_at).format('YYYY/MM/DD HH:mm');
                    c.id = conclusion.id;
                    c.slug = conclusion.slug;

                    cArray.push(c);
                });

                return cArray;
            },

            rows: function() {
                return this.mConclusions.length;
            }
        },

        data() {
            return {
                perPage: 10,
                currentPage: 1,
                sortBy: 'date',
                sortDesc: true,
                consultation_id: this.$route.params.consultation,
                fields: [
                    {
                        key: 'reference',
                        label: this.$i18n.t('message.reference'),
                    },
                    {
                        key: 'description',
                        label: this.$i18n.t('message.description'),
                    },
                    {
                        key: 'date',
                        label: this.$i18n.t('message.createdAt'),
                        sortable: true
                    },
                    {
                        key: 'action',
                        label: this.$i18n.tc('message.operations', 1)
                    }
                ],
                offset: 0,
                limit: -1,
            }
        },

        methods: {
            getConclusions() {
                let args = {
                    offset: this.offset,
                    limit: this.limit,
                    consultation: this.consultation_id
                };

                this.$store.dispatch('getConclusions', { args : args });
            },

            formattedDisplay (result) {
                return result.reference;
            },

            rowSelected(items) {
                this.$router.push({ name: 'conclusion', params: { conclusion: items[0].slug } });
            },

            itemSelected(item) {
                this.$router.push({ name: 'conclusion', params: { conclusion: item.selectedObject.slug } });
            },

            deleteConclusion(conclusion) {
                let self = this;

                let args = {
                    conclusion: conclusion
                };

                let successMessage = this.$i18n.t('message.conclusionDeleted');
                let errorMessages = {
                    generic: this.$i18n.t('message.genericError'),
                    error: this.$i18n.t('message.error')
                };

                this.$store.dispatch('deleteConclusion', { args, index: conclusion, successMessage, errorMessages })
                    .then(() => {
                        self.$bvModal.hide('delete-modal-' + conclusion);
                    });
            }
        },

        mounted() {
            // Fetch conclusion list
            this.getConclusions();
        }
    }
</script>

<style scoped>

</style>